import { useFormInstance, useSelectiveFormData } from '@neo/blocks/forms/form-provider'
import { Button } from '@neo/index'
import { cn } from '@neo/lib/utils'
import { Tooltip } from '@neo/primitives/tooltip'
import { useNodesData } from '@xyflow/react'
import isEqual from 'lodash/isEqual'
import omit from 'lodash/omit'
import { Loader2, Sparkles } from 'lucide-react'
import { useState } from 'react'
import { FlowHistoryEvent, useFlowHistoryManager } from '../hooks/use-flow-history'
import { useGenerateNodeMetadataOptimistically } from '../hooks/use-generate-node-metadata-optimistically'
import { useTypedReactFlow } from '../hooks/use-react-flow'
import { useFlowStore } from '../store'
import type { FlowNodeTypes, GenericNode } from '../types'
import { AI_METADATA_KEYS } from '../utils'

type NodeConfig = GenericNode['data']['config']

/**
 * The whole naming affordance: a ghost sparkle inside the Name input that hands
 * naming back to the AI. Rendered only while the name is user-pinned; revealed on
 * hover/focus of the field, and shown persistently once the open form carries config
 * edits (the one moment the pinned name is likely stale). Clicking regenerates from
 * the live form values and clears the pin only if generation succeeds.
 */
export const NodeNameRegenButton = ({
  nodeId,
  nodeType,
}: {
  nodeId: string
  nodeType: FlowNodeTypes
}) => {
  const node = useNodesData<GenericNode>(nodeId)
  const isEditing = useFlowStore((state) => state.isEditing)
  const form = useFormInstance<NodeConfig>(nodeType)
  const formValues = useSelectiveFormData<NodeConfig, NodeConfig>(nodeType, (values) => values)
  const { updateNodeData, getNodes } = useTypedReactFlow()
  const generateNodeMetadataOptimistically = useGenerateNodeMetadataOptimistically()
  const { saveStateToHistory } = useFlowHistoryManager()
  const [isRegenerating, setIsRegenerating] = useState(false)

  if (!node || node.data.name_source !== 'user' || !isEditing) return null

  const formDefaults = form?.options.defaultValues
  const hasConfigEdits = Boolean(
    formValues &&
      formDefaults &&
      !isEqual(omit(formValues, AI_METADATA_KEYS), omit(formDefaults, AI_METADATA_KEYS)),
  )

  const handleClick = async () => {
    setIsRegenerating(true)
    try {
      // Name what is on screen, not the last-committed config — the button often
      // appears precisely because the open form holds unsaved config edits.
      const config = { ...node.data.config, ...(formValues ?? {}) }
      const applied = await generateNodeMetadataOptimistically(
        nodeId,
        nodeType,
        config,
        getNodes(),
        updateNodeData,
        { overridePin: true },
      )
      if (applied) {
        if (form) {
          // Only sync the name when the AI's name was actually applied — on a
          // rejected name the field may hold the user's in-progress edit.
          if (applied.aiNameApplied) form.setFieldValue('name', applied.name)
          if (applied.description !== undefined) {
            form.setFieldValue('description', applied.description)
          }
        }
        saveStateToHistory(FlowHistoryEvent.NodeDataChange)
      }
    } finally {
      setIsRegenerating(false)
    }
  }

  return (
    <Tooltip content="Auto-name" delayDuration={300}>
      <Button
        type="button"
        variant="ghost"
        size="iconXs"
        data-testid="button-auto-name"
        aria-label="Let AI name this node"
        disabled={isRegenerating}
        onClick={handleClick}
        className={cn(
          'text-tertiary hover:text-secondary transition-opacity duration-150',
          // Hidden until the field is hovered/focused; the arbitrary variants hang off
          // the `relative` wrapper the Neo Input renders around input + endIcon.
          hasConfigEdits || isRegenerating
            ? 'opacity-100'
            : 'opacity-0 [.relative:hover_&]:opacity-100 [.relative:focus-within_&]:opacity-100 focus-visible:opacity-100',
        )}
      >
        {isRegenerating ? <Loader2 size={14} className="animate-spin" /> : <Sparkles size={14} />}
      </Button>
    </Tooltip>
  )
}
